// Bezug → "Nächster Bezug" (board "Claude: beim nächsten Bezug"): Claude's open suggestion on the newest shot as one
// row with "Übernehmen"; the row's text opens that shot's page. Adopted, "Mahlgrad 2,3 von Claude, zum Bezug von …"
// with "Rückgängig" (in this session). Decisions in src/ai/view.ts (nextClaudeView).
import { useEffect, useState } from 'react';
import { nextClaudeView } from '../../ai/view.js';
import { Button, Collapse, Icon } from '../components/index.js';
import { useMachine, useStore } from '../machine-context.js';
import { useApply } from './ApplyBox.js';
import { useAssistant } from './use-assistant.js';

export function NextClaude({ onOpenShot }: { onOpenShot: (id: string) => void }) {
  const { ctx, client, machineId, send } = useMachine();
  const { assistant, view } = useAssistant();
  const shots = useStore(client.shots(machineId));
  const beans = useStore(client.beans(machineId));
  const adopt = useApply();
  const [undoing, setUndoing] = useState(false);
  const shot = shots.items[0] ?? null;
  const hasKey = !!view.account?.key;
  const id = shot?.id ?? null;
  useEffect(() => {
    if (hasKey && id) void assistant.loadThread(machineId, id);
  }, [assistant, machineId, id, hasKey]);
  const v = shot
    ? nextClaudeView(
        ctx,
        shot,
        view,
        assistant.shot(machineId, shot.id),
        beans.items.find((b) => b.current) ?? null,
        adopt.sending,
      )
    : null;
  const apply = () => {
    if (!shot || !v?.apply) return;
    const index = v.index;
    void adopt.apply(v.apply, (a) => void assistant.markApplied(machineId, shot.id, index, a.changes));
  };
  const undo = async () => {
    if (!shot || !v?.undo || undoing) return;
    setUndoing(true);
    const outcome = await send(v.undo);
    if (outcome.result === 'done') await assistant.markUndone(machineId, shot.id, v.index);
    setUndoing(false);
  };
  return (
    <Collapse open={v !== null}>
      {v && shot && (
        <div className={`ai-next is-${v.state}`} data-testid="claude-next">
          <Icon name="sparkle" size="sm" className="ai-box-mark" />
          {v.state === 'pending' ? (
            <>
              <button
                type="button"
                className="ai-next-text"
                onClick={() => onOpenShot(shot.id)}
                data-testid="claude-next-open"
              >
                <span>{v.text}</span>
                {v.sub && <span className="hint">{v.sub}</span>}
              </button>
              {v.apply && (
                <Button
                  variant="secondary"
                  size="sm"
                  className="ai-small"
                  busy={adopt.sending !== null}
                  disabled={!v.apply.enabled || adopt.sending !== null}
                  onClick={apply}
                  data-testid="claude-next-apply"
                >
                  {ctx.tr('aiApply')}
                </Button>
              )}
            </>
          ) : (
            <>
              <span className="hint ai-next-done">{v.text}</span>
              {v.undo && (
                <button
                  type="button"
                  className="link-btn"
                  disabled={undoing}
                  onClick={() => void undo()}
                  data-testid="claude-next-undo"
                >
                  {ctx.tr('aiUndo')}
                </button>
              )}
            </>
          )}
        </div>
      )}
    </Collapse>
  );
}
